import { useState, useEffect, useCallback, useContext } from 'react';
import { useSnackbar } from 'notistack';

import * as wasm from 'sched';
import { WorkspaceContext } from '../context/WorkspaceContext';
import { commandErrorMessage, commandSuccessMessage } from './commandMessages';

interface UseDatabaseReturn {
  database: wasm.WrappedDatabase | null;
  workspaceId: string;
  applyCommand: (command: wasm.DatabaseCommand) => Promise<void>;
  loading: boolean;
  safeData: any | null;
  dependencyGraph: wasm.WrappedDependencyGraph | null;
}

// Hook to access and modify the database
export function useDatabase(): UseDatabaseReturn {
  const { safeData, workspaceId, buildUrl } = useContext(WorkspaceContext);
  const [database, setDatabase] = useState<wasm.WrappedDatabase | null>(null);
  const [dependencyGraph, setDependencyGraph] = useState<wasm.WrappedDependencyGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!safeData) return;

    // Create a callback function to receive database updates
    const callback = (newDatabase: any) => {
      setDatabase(newDatabase);
    };

    // Convert callback to a JS function that Rust can call
    const jsCallback = callback as Function;

    // Create a callback function for dependency graph updates
    const dependencyGraphCallback = (graph: any) => {
      setDependencyGraph(graph);
    };

    // Convert callback to a JS function that Rust can call
    const jsDependencyGraphCallback = dependencyGraphCallback as Function;

    // Subscribe to database changes - this is an async function
    const subscribeToDatabase = async () => {
      try {
        setLoading(true);
        await wasm.DataManager.select_database(safeData, jsCallback);
        await wasm.DataManager.select_dependency_graph(safeData, jsDependencyGraphCallback);

        setLoading(false);
      } catch (err) {
        console.error('Error subscribing to database changes:', err);
        // Initialize with empty structure to avoid errors
        setDatabase(null);
        setLoading(false);
      }
    };

    subscribeToDatabase();

    // Cleanup function to unsubscribe
    return () => {
      if (safeData && (safeData as any).__wbg_ptr !== 0) {
        // This is also async, but we can't await in cleanup function
        // Just fire and forget, but log any errors
        wasm.DataManager.unsubscribe(safeData, jsCallback)
          .catch((err: any) => console.error('Error unsubscribing:', err));
      }
    };
  }, [safeData]);

  // Function to apply commands to the database
  const applyCommand = useCallback(
    async (command: wasm.DatabaseCommand) => {
      if (!safeData) return;
      try {
        await wasm.DataManager.apply_command(safeData, command);

        // Success messages may include JSX (e.g. a link to a newly created
        // entity's detail page) — see `commandMessages.tsx`. Bump the
        // duration on Create so the user has time to click the link.
        const successMessage = commandSuccessMessage(command, buildUrl);
        const isCreate = (command as any).type === 'CreateItem';
        enqueueSnackbar(successMessage, {
          variant: 'success',
          autoHideDuration: isCreate ? 8000 : undefined,
        });
      } catch (err) {
        const errorMessage = commandErrorMessage(command, err);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        console.error('Error applying command:', err);
        throw err; // Re-throw to allow handling in the calling function
      }
    },
    [safeData, enqueueSnackbar, buildUrl]
  );

  return {
    database,
    workspaceId,
    applyCommand,
    loading,
    safeData,
    dependencyGraph,
  };
}
